import S from './UserManagement.module.scss';
import React, { useEffect, useState } from 'react';
import { AdminUserType } from '../../../types/AdminTypes';
import { fetchAdminUsers, updateAdminResumeScore } from '../../../repository/AdminRepository';
import ResumeModal from '../ResumeModal';
import { InputUtils } from '../../../utils/InputUtils';

interface UserManagementProps {
  masterToken: string;
}

const UserManagement = (props: UserManagementProps) => {
  const { masterToken } = props;
  // 검색어 및 테이블에 표시될 데이터 상태 관리
  const ADMIN_TOKEN = process.env.REACT_APP_IVY_ADMIN_TOKEN || '';
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [ivyUsers, setIvyUsers] = useState<AdminUserType[]>([]);
  const [isOpenViewer, setIsOpenViewer] = useState<boolean>(false);
  const [editedScores, setEditedScores] = useState<number[]>([]);
  const [currentActiveUserId, setCurrentActiveUserId] = useState<number>(0);

  const maxPageButtons = 10;
  const currentPageGroup = Math.floor(currentPage / maxPageButtons);
  const startPage = currentPageGroup * maxPageButtons;
  const endPage = Math.min(startPage + maxPageButtons, totalPages);
  const pages = Array.from({ length: endPage - startPage }, (_, index) => startPage + index);

  const openResumeViewer = (id: number) => {
    setIsOpenViewer(true);
    setCurrentActiveUserId(id);
  };

  const editScore = (id: number, score: number, index: number) => {
    if (score === undefined) {
      alert('스코어를 정확하게 입력하세요');
      return;
    }
    updateAdminResumeScore(id, ADMIN_TOKEN, score)
      .then((res) => {
        if (res) {
          alert('점수 반영 완료');
          editedScores[index] = 0;
          getAdminUsers(searchTerm, currentPage, 10, ADMIN_TOKEN);
        }
      })
      .catch((e) => console.error(e));
  };

  const getAdminUsers = (name: string, page: number, size: number, token: string) => {
    fetchAdminUsers(name, page, size, token)
      .then((res) => {
        setIvyUsers(res.content);
        setTotalElements(res.totalElements);
        setTotalPages(res.totalPages);
      })
      .catch((e) => console.error(e));
  };

  // 검색어 입력시 데이터 필터링
  const handleSearch = (e: any) => {
    setSearchTerm(e.target.value);
  };

  const handleScoreChange = (index: number, newScore: number) => {
    setEditedScores((prevScores) => {
      const newEditedScores = [...prevScores]; // 이전 스코어 배열을 복사하여 새로운 배열 생성
      newEditedScores[index] = newScore; // 주어진 인덱스의 스코어를 새로운 값으로 업데이트
      return newEditedScores; // 새로운 스코어 배열 반환
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    getAdminUsers(searchTerm, page, 10, ADMIN_TOKEN);
  };

  useEffect(() => {
    getAdminUsers(searchTerm, 0, 10, ADMIN_TOKEN);
  }, []);

  if (masterToken !== ADMIN_TOKEN) {
    return null;
  }

  return (
    <>
      {isOpenViewer && (
        <ResumeModal id={currentActiveUserId} closeModal={() => setIsOpenViewer(false)} />
      )}
      <div className={S.admin_page}>
        <h1>사용자 관리</h1>
        <div className={S.search_container}>
          <input
            className={S.search_name}
            type="text"
            placeholder="이름 검색"
            value={searchTerm}
            onChange={handleSearch}
          />
          <div className={S.empty}></div>
          <button
            className={S.search_btn}
            onClick={() => {
              setCurrentPage(0);
              getAdminUsers(searchTerm, 0, 10, ADMIN_TOKEN);
            }}
          >
            검색
          </button>
          <p className={S.total}>전체 유저 수 {InputUtils.numberWithCommas(totalElements)}</p>
        </div>
        <table>
          <thead>
            <tr>
              <th>순번</th>
              <th>이름</th>
              <th>이메일</th>
              <th>Phone</th>
              <th>점수</th>
              <th>점수 수정</th>
              <th>반영</th>
            </tr>
          </thead>
          <tbody>
            {ivyUsers?.map((item, index) => (
              <tr key={item.id}>
                <td>{index + 1 + currentPage * 10}</td>
                <td
                  onClick={() => {
                    item.resumeId && openResumeViewer(item.resumeId);
                  }}
                >
                  {item.resumeId ? (
                    <>
                      <span className={S.table_user_name}>{item.name}</span>
                    </>
                  ) : (
                    <>{item.name}</>
                  )}
                </td>
                <td>{item.email}</td>
                <td>{item.phoneNumber}</td>
                <td>{item.score || 0}</td>
                <td>
                  <input
                    className={S.input_num}
                    type="number"
                    onChange={(e) => {
                      handleScoreChange(index, parseFloat(e.target.value));
                    }}
                  />
                </td>
                <td>
                  {item.resumeId ? (
                    <button
                      className={S.default_button}
                      onClick={() => {
                        editScore(item.resumeId, editedScores[index], index);
                      }}
                    >
                      점수 반영
                    </button>
                  ) : (
                    <p className={S.resume_not_found}>이력서 미작성</p>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className={S.div_vertical_blank}></div>
        <div className={S.pagination}>
          {currentPageGroup > 0 && (
            <button onClick={() => handlePageChange(startPage - 1)} className={S.page_button}>
              &lt; 10개
            </button>
          )}
          {pages.map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={currentPage === page ? S.selected_button : S.page_button}
            >
              {page + 1}
            </button>
          ))}
          {currentPageGroup < Math.ceil(totalPages / maxPageButtons) - 1 && (
            <button onClick={() => handlePageChange(endPage)} className={S.page_button}>
              10개 &gt;
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default UserManagement;
